import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const OfferContext = createContext();
export const useOffers = () => useContext(OfferContext);

export const OfferProvider = ({ children }) => {
  const { token, adminId, isReady } = useAuth();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const OFFERS_ENDPOINT = "/promo-codes";
  const baseURL = BASE_URL.endsWith("/")
    ? BASE_URL.slice(0, -1)
    : BASE_URL;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetOffers = (updater) => {
    if (!mountedRef.current) return;
    setOffers(updater);
  };

  const normalizeOffer = (offer) => ({
    ...offer,
    category_id: offer.category_id
      ? offer.category_id.toString().split(",").map(Number)
      : [],
    product_id: offer.product_id
      ? offer.product_id.toString().split(",").map(Number)
      : [],
  });

  /* ---------------- FETCH OFFERS ---------------- */

  const fetchOffers = useCallback(async () => {
    setLoading(true);

    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${OFFERS_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json();

      if (!res.ok) {
        throw new Error(payload?.message || "Fetch failed");
      }

      const items = Array.isArray(payload?.data) ? payload.data : [];

      safeSetOffers(items.map(normalizeOffer));
    } catch (err) {
      console.error("Fetch Offers Error:", err);
      safeSetOffers([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL]);

  /* ---------------- CREATE OFFER ---------------- */

  const addOffer = useCallback(
    async (offerData) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const payloadData = {
          ...offerData,
          category_id: offerData.category_id || [],
          product_id: offerData.product_id || [],
        };

        const res = await fetch(`${baseURL}${OFFERS_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payloadData),
        });

        const payload = await res.json();

        if (!res.ok) {
          throw new Error(payload?.message || "Offer creation failed");
        }

        await fetchOffers();

        return payload;
      } catch (err) {
        console.error("Add Offer Error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchOffers]
  );

  /* ---------------- UPDATE OFFER ---------------- */

  const updateOffer = useCallback(
    async (id, offerData) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const payloadData = {
          ...offerData,
          category_id: offerData.category_id || [],
          product_id: offerData.product_id || [],
        };

        const res = await fetch(`${baseURL}${OFFERS_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payloadData),
        });

        const payload = await res.json();

        if (!res.ok) {
          throw new Error(payload?.message || "Update failed");
        }

        await fetchOffers();

        return payload;
      } catch (err) {
        console.error("Update Offer Error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchOffers]
  );

  /* ---------------- DELETE OFFER ---------------- */

  const deleteOffer = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const auditId =
          localStorage.getItem("admin_id") || adminId || null;

        const res = await fetch(`${baseURL}${OFFERS_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: auditId,
          }),
        });

        const payload = await res.json();

        if (!res.ok) {
          throw new Error(payload?.message || "Delete failed");
        }

        safeSetOffers((prev) => prev.filter((o) => o.id !== id));

        return payload;
      } catch (err) {
        console.error("Delete Offer Error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- TOGGLE STATUS ---------------- */

  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus =
        currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        const auditId =
          localStorage.getItem("admin_id") || adminId || null;

        safeSetOffers((prev) =>
          prev.map((o) =>
            o.id === id ? { ...o, status: newStatus } : o
          )
        );

        const res = await fetch(
          `${baseURL}${OFFERS_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: auditId,
            }),
          }
        );

        const payload = await res.json();

        if (!res.ok) {
          throw new Error(payload?.message || "Status update failed");
        }

        return payload;
      } catch (err) {
        console.error("Toggle Status Error:", err);

        safeSetOffers((prev) =>
          prev.map((o) =>
            o.id === id ? { ...o, status: currentStatus } : o
          )
        );

        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    if (!isReady) return;
    if (!token) return;

    fetchOffers().catch((err) =>
      console.warn("Initial fetch failed:", err.message)
    );
  }, [isReady, token]);

  return (
    <OfferContext.Provider
      value={{
        offers,
        loading,
        fetchOffers,
        addOffer,
        updateOffer,
        deleteOffer,
        toggleStatus,
      }}
    >
      {children}
    </OfferContext.Provider>
  );
};